// Helpers for birthday / work-anniversary highlighting on the In/Out board.
// Highlight ("flash") when the date falls within the next 3 days or the past 2 days.

import { buildHolidaySet, nextBusinessDay } from "@/lib/canadaHolidays";

const NEXT_DAYS = 3;
const PAST_DAYS = 2;
// Iter 265 — the window is configurable in Settings → Dashboard →
// Celebrations and arrives on `/auth/me` as `celebrations.days_ahead`. These
// are the fallbacks for the moment before that lands.
export const DEFAULT_DAYS_AHEAD = 7;

function daysUntilAnnual(month, day) {
  if (!month || !day) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let next = new Date(now.getFullYear(), month - 1, day);
  // signed distance: pick the occurrence closest to today (handles year wrap)
  const candidates = [
    new Date(now.getFullYear() - 1, month - 1, day),
    next,
    new Date(now.getFullYear() + 1, month - 1, day),
  ];
  let best = null;
  for (const c of candidates) {
    const diff = Math.round((c - today) / 86400000);
    if (best === null || Math.abs(diff) < Math.abs(best)) best = diff;
  }
  return best; // 0 = today, positive = upcoming, negative = passed
}

// birthday stored as "MM-DD"
export function birthdayStatus(mmdd) {
  if (!mmdd) return null;
  const [m, d] = mmdd.split("-").map((n) => parseInt(n, 10));
  const diff = daysUntilAnnual(m, d);
  if (diff === null) return null;
  const flash = diff <= NEXT_DAYS && diff >= -PAST_DAYS;
  const label = diff === 0 ? "Birthday today!" : diff > 0 ? `Birthday in ${diff}d` : `Birthday was ${-diff}d ago`;
  return { flash, diff, label, today: diff === 0 };
}

// work anniversary stored as "YYYY-MM-DD"
export function anniversaryStatus(ymd) {
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  const diff = daysUntilAnnual(m, d);
  if (diff === null) return null;
  const years = new Date().getFullYear() - y;
  const flash = diff <= NEXT_DAYS && diff >= -PAST_DAYS;
  const label = diff === 0 ? `${years}-year anniversary today!` : diff > 0 ? `${years}-yr anniversary in ${diff}d` : `${years}-yr anniversary ${-diff}d ago`;
  return { flash, diff, years, label, today: diff === 0 };
}

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ── Company anniversary (hard-coded) ────────────────────────────────────
// UnitedCloud was founded September 16, 2014 → turns 12 on Sept 16, 2026.
// Shown on the dashboard during September only.
export const COMPANY_NAME = "UnitedCloud";
export const COMPANY_FOUNDED = { year: 2014, month: 9, day: 16 };

export function companyAnniversary(now = new Date()) {
  const { year: foundedYear, month, day } = COMPANY_FOUNDED;
  if (now.getMonth() + 1 !== month) return null;
  const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(now.getFullYear(), month - 1, day);
  const diff = Math.round((target - today0) / 86400000);
  const years = now.getFullYear() - foundedYear;
  const plural = (n) => `${n} day${n === 1 ? "" : "s"}`;
  return {
    name: COMPANY_NAME,
    years,
    diff,
    today: diff === 0,
    dateLabel: `${MONTHS[month - 1]} ${day}`,
    label: diff === 0
      ? `${COMPANY_NAME} is ${years} years old today!`
      : diff > 0
        ? `${COMPANY_NAME} turns ${years} in ${plural(diff)} (${MONTHS[month - 1]} ${day})`
        : `${COMPANY_NAME} turned ${years} ${plural(-diff)} ago (${MONTHS[month - 1]} ${day})`,
  };
}

// "09-16" → "Sep 16"
export function formatBirthday(mmdd) {
  if (!mmdd) return "";
  const [m, d] = mmdd.slice(-5).split("-").map((n) => parseInt(n, 10));
  if (!m || !d) return "";
  return `${MONTHS[m - 1]} ${d}`;
}

// "2014-09-16" → { dateLabel: "Sep 16, 2014", years: 12 }
export function formatAnniversary(ymd, now = new Date()) {
  if (!ymd || ymd.length < 10) return null;
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  // Years completed as of today (anniversary later this year → not yet counted).
  const passed = new Date(now.getFullYear(), m - 1, d) <= new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const years = now.getFullYear() - y - (passed ? 0 : 1);
  return { dateLabel: `${MONTHS[m - 1]} ${d}, ${y}`, years: Math.max(0, years) };
}

// Iter 101 — Badge helpers for InOut board / WhoIsInDrawer sidebar.
//
// These are DISTINCT from `birthdayStatus`/`anniversaryStatus` above, which
// use a symmetric ±2/+3 window against the raw date and are kept for any
// legacy callers.
//
// Iter 265 — both badges now follow the ONE configurable window
// (`daysAhead`): the day itself through N days ahead, upcoming only. Birthdays
// land on their observed (Canadian-holiday-aware) date, anniversaries on their
// historical day so the years line up.

export function birthdayBadgeStatus(mmdd, daysAhead = DEFAULT_DAYS_AHEAD) {
  if (!mmdd) return null;
  const [m, d] = mmdd.split("-").map((n) => parseInt(n, 10));
  if (!m || !d) return null;
  const t = celebrationTiming(m, d, daysAhead);
  if (!t) return null;
  return { flash: t.show, diff: t.diff, today: t.today, deferred: t.deferred, label: `Birthday · ${t.when}` };
}

export function anniversaryBadgeStatus(ymd, daysAhead = DEFAULT_DAYS_AHEAD) {
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  if (!m || !d) return null;
  const t = celebrationTiming(m, d, daysAhead);
  if (!t) return null;
  const years = new Date().getFullYear() - y;
  return { flash: t.show, diff: t.diff, years, today: t.today, deferred: t.deferred, label: `${years}-yr anniversary · ${t.when}` };
}

// Iter 101 — Compute the "observed" occurrence of an annual event
// (birthday / anniversary) relative to today, taking Canadian
// statutory holidays and weekends into account. Returns:
//   { effDiff, actualDiff, deferred, observedDate }
// where `effDiff` is the diff from *today* to the observed business day
// (>=0 for upcoming or today, negative for past occurrences of the
// observed date). `actualDiff` is the raw diff (unadjusted) — kept so
// callers can still show "actual birthday was Saturday" in tooltips.
function nextObservedOccurrence(month, day, holidaySet, today) {
  if (!month || !day) return null;
  const candidates = [];
  for (const yr of [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1]) {
    const actual = new Date(yr, month - 1, day);
    const observed = nextBusinessDay(actual, holidaySet);
    candidates.push({ actual, observed });
  }
  // Pick the observed occurrence closest to today (biased upcoming).
  const today0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  let best = null;
  for (const c of candidates) {
    const effDiff = Math.round((c.observed - today0) / 86400000);
    // Prefer non-negative diffs, then smallest absolute value.
    const score = effDiff < 0 ? 10000 - effDiff : effDiff;
    if (best === null || score < best.score) best = { ...c, effDiff, score };
  }
  const actualDiff = Math.round((best.actual - today0) / 86400000);
  const deferred = best.observed.getTime() !== new Date(best.actual.getFullYear(), best.actual.getMonth(), best.actual.getDate()).getTime();
  return { effDiff: best.effDiff, actualDiff, deferred, observedDate: best.observed, actualDate: best.actual };
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const fmtDay = (dt) => `${DOW[dt.getDay()]} ${MONTHS[dt.getMonth()]} ${dt.getDate()}`;
const inDays = (n) => (n === 0 ? "Today" : n === 1 ? "Tomorrow" : `in ${n} days`);

// Countdown to the REAL date; weekend/holiday events are celebrated on the
// next business day, shown as "· celebrated Mon Oct 19" and "Celebrating today".
export function celebrationTiming(month, day, daysAhead = DEFAULT_DAYS_AHEAD, today = new Date()) {
  const occ = nextObservedOccurrence(month, day, buildHolidaySet(today), today);
  if (!occ) return null;
  const { actualDiff, effDiff, deferred } = occ;
  const upcoming = actualDiff >= 0 && actualDiff <= daysAhead;
  const show = upcoming || (deferred && effDiff >= 0 && effDiff <= daysAhead);
  const celebrated = deferred ? `celebrated ${fmtDay(occ.observedDate)}` : "";
  let when;
  if (deferred && effDiff === 0) when = `Celebrating today (was ${fmtDay(occ.actualDate)})`;
  else if (actualDiff >= 0) when = `${inDays(actualDiff)}${actualDiff ? ` (${fmtDay(occ.actualDate)})` : ""}${celebrated ? ` · ${celebrated}` : ""}`;
  else when = `Was ${fmtDay(occ.actualDate)} · ${celebrated}`;
  return {
    show, when, deferred,
    diff: actualDiff >= 0 ? actualDiff : effDiff,
    today: deferred ? effDiff === 0 : actualDiff === 0,
  };
}

// Iter 101 — Banner only shows current + upcoming celebrations (no past).
// Window: today (diff=0) → `daysAhead` (Settings → Dashboard → Celebrations).
// Weekend/holiday birthdays are deferred to the next business day and
// displayed on that observed date.
export function celebrationsThisWeek(staff = [], opts = {}) {
  const daysAhead = opts.daysAhead ?? DEFAULT_DAYS_AHEAD;
  const includeAnniversaries = opts.includeAnniversaries !== false;
  const out = [];
  const today = new Date();
  for (const s of staff) {
    const name = (s.name || "").trim();

    // ── Birthday ─────────────────────────────────────────────────
    if (s.birthday) {
      const [bm, bd] = s.birthday.split("-").map((n) => parseInt(n, 10));
      const t = celebrationTiming(bm, bd, daysAhead, today);
      if (t?.show) {
        out.push({ user_id: s.user_id, name, type: "birthday", diff: t.diff, today: t.today,
                   deferred: t.deferred, when: t.when, label: `Birthday · ${t.when}` });
      }
    }

    // ── Work anniversary ─────────────────────────────────────────
    if (s.work_anniversary && includeAnniversaries) {
      const [y, m, d] = s.work_anniversary.split("-").map((n) => parseInt(n, 10));
      const t = celebrationTiming(m, d, daysAhead, today);
      if (t?.show) {
        const years = today.getFullYear() - y;
        out.push({ user_id: s.user_id, name, type: "anniversary", diff: t.diff, today: t.today, years,
                   deferred: t.deferred, when: t.when, label: `${years}-yr anniversary · ${t.when}` });
      }
    }
  }
  return out.sort((x, y) => x.diff - y.diff);
}
